import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const FONT_LINK_ID = "privacy-policy-fonts";

export default function PrivacyAndPolicy() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Snap Solutions | Privacy Policy";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta) {
      meta.setAttribute("content", "Snap Solutions — Privacy Policy.");
    }

    if (!document.getElementById(FONT_LINK_ID)) {
      const link = document.createElement("link");
      link.id = FONT_LINK_ID;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;600;700&display=swap";
      document.head.appendChild(link);
    }

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) {
        meta.setAttribute("content", prevDesc || "");
      }
    };
  }, []);

  const logoSrc = `${process.env.PUBLIC_URL}/assets/img/snap-logo-new.png`;

  return (
    <div className="terms-of-use-page">
      <div className="terms-of-use-wrap">
        <Link to="/login" className="terms-back-to-login" aria-label="Back to sign in">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to sign in
        </Link>

        <header className="terms-top-header">
          <div className="terms-brand-logo">
            <img src={logoSrc} alt="Snap Solutions Logo" />
          </div>
          <div className="terms-header-meta">
            <span className="terms-header-meta-label">Legal &amp; Compliance</span>
          </div>
        </header>

        <section className="terms-hero">
          <span className="terms-badge-pill">Legal Notice</span>
          <h1>
            Snap Solutions Platform
            <br />
            Privacy Policy
          </h1>
          <div className="terms-hero-meta">
            <span>Effective Date: May 5, 2026</span>
            <span>Last Modified: May 5, 2026</span>
            <span>Version: 1.0</span>
          </div>
        </section>

        <section className="terms-card">
          <div className="terms-legal-content">
            <h2>1. About This Privacy Policy</h2>
            <h3>1.1 Purpose</h3>
            <p>This Privacy Policy describes how Snap handles information in connection with the Platform.</p>
            <p>
              The Platform is a business-to-business healthcare SaaS platform used by clinics, occupational health
              providers, and related organizations to support clinical, occupational medicine, employer-service, workforce
              health, compliance, billing, and administrative workflows.
            </p>

            <h3>1.2 Core Data Structure</h3>
            <p>Snap handles information through three distinct data tracks:</p>
            <ul>
              <li>Track A — Customer Data and Protected Health Information</li>
              <li>Track B — Service Metadata and User Telemetry</li>
              <li>Track C — Employer Portal Data</li>
            </ul>
            <p>
              These data tracks are legally distinct. Snap’s rights, obligations, and role differ depending on the data
              track involved.
            </p>

            <h3>1.3 No Consumer Health App Relationship</h3>
            <p>
              The Platform is not a direct-to-consumer health application. Individual patients do not create consumer
              accounts with Snap for personal health management through this Privacy Policy.
            </p>

            <h2>2. Track A — Customer Data and Protected Health Information</h2>
            <h3>2.1 What Track A Includes</h3>
            <p>
              Customer Data means data submitted to, uploaded to, created in, received by, maintained in, or transmitted
              through the Platform by or on behalf of the Customer.
            </p>
            <p>
              Customer Data may include PHI, patient records, clinical documentation, occupational health records, visit
              documentation, lab results, drug and alcohol testing records, DOT physical examination records, billing
              records, scheduling records, employer-service records, uploaded documents, and other data controlled by the
              Customer.
            </p>

            <h3>2.2 Snap’s Role as Business Associate</h3>
            <p>
              When Customer Data includes PHI, Snap processes that PHI as a Business Associate of the Customer under
              HIPAA and the BAA.
            </p>
            <p>
              Snap does not determine the purposes or means of processing PHI. The Customer, as the Covered Entity or
              otherwise legally responsible organization, controls PHI and determines how PHI is created, used,
              disclosed, retained, amended, exported, or deleted, subject to applicable law and the BAA.
            </p>

            <h3>2.3 PHI Is Not Independently Commercialized</h3>
            <p>
              Snap does not sell, rent, or independently commercialize PHI. Snap does not use PHI for marketing purposes
              unless expressly permitted by HIPAA, the BAA, and applicable law.
            </p>

            <h3>2.4 Patient Rights Requests</h3>
            <p>
              Because Snap acts as a Business Associate and not as the Covered Entity, Snap generally cannot directly
              fulfill HIPAA individual rights requests, including requests for access, amendment, restriction, accounting
              of disclosures, or deletion of PHI.
            </p>
            <p>
              Patients and individuals seeking to exercise rights regarding PHI should direct those requests to the
              Customer or healthcare provider responsible for the record.
            </p>

            <h3>2.5 HITECH and Electronic Access</h3>
            <p>
              Where applicable, the Customer is responsible for responding to patient requests for electronic access to
              PHI. Snap may support Customer-configured export, access, and record-production workflows through the
              Platform, subject to the MSA, BAA, Customer configuration, and applicable law.
            </p>

            <h3>2.6 Clinical User Privacy Questions</h3>
            <p>
              Clinical and Administrative Users with PHI-related questions should contact the Customer’s Privacy Officer
              or compliance contact.
            </p>

            <h3>2.7 Substance Use Disorder Records Scope Limitation</h3>
            <p>
              The Platform is designed and operated for occupational health workflows. Snap does not maintain records from
              substance abuse treatment, counseling, or referral programs.
            </p>
            <p>
              Drug and alcohol testing results available through the Platform are limited to employer-directed
              occupational health testing. Records subject to 42 C.F.R. Part 2 are outside the scope of the Platform.
            </p>

            <h2>3. Track B — Service Metadata and User Telemetry</h2>
            <h3>3.1 What Track B Includes</h3>
            <p>
              Service Metadata and User Telemetry mean technical, operational, security, and usage information generated
              by your interaction with the Platform.
            </p>
            <p>This may include:</p>
            <ul>
              <li>Login and authentication events.</li>
              <li>User ID, timestamp, IP address, device type, browser type, and MFA verification status.</li>
              <li>Session duration, navigation paths, and feature usage.</li>
              <li>Platform performance metrics, latency data, diagnostics, and error logs.</li>
              <li>Audit log entries showing data accessed, created, modified, exported, printed, or deleted.</li>
              <li>Security event logs, failed login attempts, and anomalous access patterns.</li>
              <li>Workflow completion data, form submission metadata, and interface interaction signals.</li>
              <li>AI Feature interaction metadata and AI Output selection events.</li>
            </ul>

            <h3>3.2 What Track B Excludes</h3>
            <p>
              Track B does not include the substantive content of patient records, clinical notes, PHI, medical
              documentation, or employer medical records.
            </p>
            <p>
              Service Metadata describes how the Platform is accessed and used; it does not independently include the
              clinical substance of what is documented within patient or occupational health records.
            </p>

            <h3>3.3 Snap’s Role for Track B</h3>
            <p>
              Snap may process Service Metadata and User Telemetry as an independent controller or business operator for
              Platform operations, security, audit, support, analytics, compliance, and improvement purposes, subject to
              the MSA, BAA, and applicable law.
            </p>

            <h2>4. Track C — Employer Portal Data</h2>
            <h3>4.1 What Track C Includes</h3>
            <p>
              Employer Portal Data means occupational health outputs, workforce compliance information, employer-service
              data, and related records made available to Employer Portal Users through the Employer Command Center or
              other employer-facing Platform features.
            </p>
            <p>Employer Portal Data may include, where configured and authorized by the Customer:</p>
            <ul>
              <li>Work status reports.</li>
              <li>Fitness-for-duty outcomes.</li>
              <li>Occupational health examination results or summaries.</li>
              <li>DOT medical certificate status or expiration information.</li>
              <li>Drug and alcohol testing results or status.</li>
              <li>Vaccine compliance status.</li>
              <li>Audiogram compliance records.</li>
              <li>Respirator clearance determinations.</li>
              <li>Medical surveillance outcomes.</li>
              <li>Scheduling and appointment status.</li>
              <li>Employer-service documents, invoices, and related records.</li>
            </ul>

            <h3>4.2 Legal Character of Track C</h3>
            <p>
              Employer Portal Data may originate as PHI in the Customer’s system. The Customer determines what
              information is made available to employer-side users through the Platform.
            </p>
            <p>
              Once occupational health information is disclosed to an employer through the Employer Command Center, the
              employer’s use, retention, disclosure, and employment-related handling of that information may be governed by
              laws outside HIPAA, including ADA confidentiality rules, DOT regulations, OSHA requirements, FMCSA
              requirements, workers’ compensation law, state drug and alcohol testing laws, state employment laws, FCRA,
              employee authorization terms, and other applicable legal requirements.
            </p>

            <h3>4.3 Snap’s Role for Track C</h3>
            <p>
              Snap provides the technology platform through which the Customer may make Employer Portal Data available
              to authorized Employer Portal Users. Snap does not determine whether an employer may lawfully request,
              access, use, retain, disclose, or act upon Employer Portal Data in any particular jurisdiction or
              employment context.
            </p>

            <h3>4.4 Employer Responsibility</h3>
            <p>
              Employer-side users and their organizations are responsible for compliance with all applicable federal,
              state, and local laws governing their use of occupational health data in the jurisdictions where their
              business operates and where their employees are located or perform work.
            </p>
            <p>
              Applicable law varies by jurisdiction and may affect what employment actions may be taken based on data
              accessed through the Employer Command Center.
            </p>

            <h3>4.5 Drug Testing Results Scope</h3>
            <p>
              Drug and alcohol testing results accessible through the Employer Command Center are limited to MRO-verified
              results from employer-directed, non-treatment testing programs, including DOT-regulated and non-DOT
              occupational testing, for which employer access is authorized under applicable law.
            </p>
            <p>
              Records from substance abuse treatment programs are outside the scope of the Platform and are not
              accessible through the Employer Command Center.
            </p>

            <h3>4.6 GINA and Genetic Information</h3>
            <p>
              Employer access to the Employer Command Center does not include and must not include genetic information,
              including family medical history.
            </p>
            <p>
              Employer-side users who believe they have received genetic information through the Platform must not use,
              retain, or disclose that information and should notify the Customer immediately.
            </p>

            <h3>4.7 OSHA Records Retention</h3>
            <p>
              Occupational health surveillance records accessible through the Employer Command Center, including audiometric
              testing results, respirator clearance determinations, and medical surveillance outcomes, may be subject to
              OSHA-mandated retention requirements under applicable standards and 29 C.F.R. § 1910.1020.
            </p>
            <p>
              Employers are responsible for maintaining independent copies of such records for the full applicable
              retention period. Platform access is contingent on the Customer’s active subscription and configuration.
            </p>

            <h3>4.8 FMCSA and NRCME Data</h3>
            <p>
              DOT physical examination results and medical examiner certificate data displayed in the Employer Command
              Center are provided for workforce compliance management. This data does not substitute for verification
              through the FMCSA National Registry, driver-provided medical certificate, or other legally required source.
            </p>
            <p>
              Medical examiner NRCME reporting obligations are governed by applicable FMCSA regulations and are independent
              of the Platform.
            </p>

            <h2>5. How Snap Uses Service Metadata and User Telemetry</h2>
            <h3>5.1 Platform Operations</h3>
            <p>
              Snap uses Service Metadata and User Telemetry to operate, maintain, troubleshoot, monitor, support, and
              improve the Platform; verify license usage; manage billing metrics; analyze performance; and provide
              Customer support.
            </p>

            <h3>5.2 Security and Incident Response</h3>
            <p>
              Snap uses Service Metadata and User Telemetry to detect, investigate, prevent, and respond to unauthorized
              access, suspicious activity, credential compromise, security incidents, privacy incidents, system misuse, and
              anomalous behavior.
            </p>

            <h3>5.3 Audit and Compliance</h3>
            <p>
              Snap uses Service Metadata and User Telemetry to maintain audit trails, support HIPAA Security Rule audit
              controls where applicable, support SOC 2 Type II compliance, respond to Customer audits, and preserve
              records required for compliance, legal, security, and assurance purposes.
            </p>

            <h3>5.4 Product Development and Analytics</h3>
            <p>
              Snap may use Service Metadata and User Telemetry to understand usage patterns, improve user experience,
              develop new features, evaluate workflow performance, benchmark system functionality, and support internal
              analytics.
            </p>

            <h3>5.5 AI Feature Improvement</h3>
            <p>
              Snap may use de-identified or aggregated Service Metadata and User Telemetry to train, tune, evaluate,
              monitor, or improve AI-enabled Platform features, consistent with the MSA, BAA, Customer authorization, and
              applicable law.
            </p>

            <h3>5.6 Legal and Regulatory Compliance</h3>
            <p>
              Snap may use or disclose Service Metadata and User Telemetry to respond to valid legal process, court
              orders, subpoenas, regulatory requests, government investigations, law enforcement requests, security
              obligations, or other legally authorized demands.
            </p>

            <h2>6. De-Identified and Aggregated Data</h2>
            <h3>6.1 De-Identification</h3>
            <p>
              Where Snap de-identifies PHI or Customer Data, Snap does so in accordance with HIPAA de-identification
              standards and the BAA.
            </p>
            <p>Once data has been properly de-identified under applicable law, it is no longer PHI.</p>

            <h3>6.2 Aggregated Data</h3>
            <p>
              Snap may create aggregated data from Service Metadata, User Telemetry, Platform usage data, or de-identified
              information for analytics, benchmarking, product development, security improvement, operational analysis, AI
              model training and tuning, and other lawful business purposes.
            </p>

            <h3>6.3 Commercialization Limits</h3>
            <p>
              Where de-identification of PHI is performed for purposes beyond direct service delivery, including
              commercialization, benchmarking, or AI development, Snap does so only as authorized by the Customer’s Order
              Form, the BAA, separate written authorization, or other legally valid authorization.
            </p>

            <h3>6.4 No Re-Identification</h3>
            <p>Snap does not attempt to re-identify de-identified data in violation of applicable law.</p>

            <h2>7. Access Logs and Administrator Review</h2>
            <h3>7.1 Access Logs</h3>
            <p>The Platform generates access logs and activity logs showing user activity within the Platform.</p>
            <p>
              These logs may include user identity, login events, IP address, device data, session duration, records
              accessed, documents viewed, reports generated, data modified, exports, print events, administrative actions,
              failed login attempts, and other activity.
            </p>

            <h3>7.2 Clinical and Administrative Users</h3>
            <p>
              For Clinical and Administrative Users, the Customer’s authorized administrators may access and review
              activity logs for workforce oversight, HIPAA compliance, security monitoring, audit, investigation, and
              administrative purposes.
            </p>

            <h3>7.3 Employer Portal Users</h3>
            <p>
              For Employer Portal Users, the clinic or healthcare organization that is the Customer may access and review
              logs of Employer Command Center activity. The employer organization may receive access to certain activity
              information only as made available by the Customer, Platform configuration, or applicable law.
            </p>

            <h3>7.4 Legal and Regulatory Use</h3>
            <p>
              Access logs may be retained, reviewed, or produced in response to legal process, regulatory inquiry,
              government audit, Customer audit request, security investigation, or other legally authorized request.
            </p>

            <h2>8. Subprocessors</h2>
            <h3>8.1 Use of Subprocessors</h3>
            <p>Snap uses third-party subcontractors and subprocessors to support Platform operations.</p>
            <p>Categories of subprocessors may include:</p>
            <ul>
              <li>Cloud infrastructure and hosting providers.</li>
              <li>Data storage and backup providers.</li>
              <li>Security monitoring and threat detection vendors.</li>
              <li>Analytics and performance monitoring tools.</li>
              <li>Payment and billing processors.</li>
              <li>Identity, authentication, and access management services.</li>
              <li>EPCS identity verification providers.</li>
              <li>Communication and notification services.</li>
              <li>Support and ticketing systems.</li>
              <li>Integration and interoperability vendors.</li>
            </ul>

            <h3>8.2 Subprocessor Obligations</h3>
            <p>
              Subprocessors are contractually required to maintain confidentiality, privacy, and security safeguards
              consistent with Snap’s obligations, as applicable.
            </p>
            <p>Snap remains responsible for subprocessor performance to the extent required by the MSA and BAA.</p>

            <h3>8.3 Current List</h3>
            <p>
              A current list of subprocessors may be requested by contacting Snap through the contact information listed
              in this Privacy Policy.
            </p>

            <h2>9. Security Measures</h2>
            <h3>9.1 Security Program</h3>
            <p>
              Snap maintains a written information security program designed to protect the confidentiality, integrity,
              and availability of the Platform.
            </p>
            <p>Security controls may include:</p>
            <ul>
              <li>Encryption in transit.</li>
              <li>Encryption at rest.</li>
              <li>Role-based access controls.</li>
              <li>Multi-factor authentication for privileged and administrative access.</li>
              <li>Audit logging.</li>
              <li>Vulnerability scanning.</li>
              <li>Security monitoring.</li>
              <li>Annual independent penetration testing.</li>
              <li>Backup and disaster recovery controls.</li>
              <li>Incident response procedures.</li>
              <li>Administrative, technical, and physical safeguards aligned with applicable security requirements.</li>
            </ul>

            <h3>9.2 SOC 2</h3>
            <p>
              Snap maintains a SOC 2 Type II security program. Service Metadata and User Telemetry may be used to
              generate and maintain audit evidence, access logs, monitoring records, and compliance documentation required
              to support SOC 2 assurance activities.
            </p>

            <h3>9.3 Security Limitations</h3>
            <p>
              No system can be guaranteed to be completely secure. Snap does not disclose detailed security architecture,
              vendor configurations, or control implementation details where disclosure could compromise security.
            </p>

            <h2>10. Data Retention</h2>
            <h3>10.1 Customer Data and PHI</h3>
            <p>
              Retention of Customer Data and PHI is governed by the MSA, BAA, Customer configuration, Customer
              instructions, and applicable law.
            </p>
            <p>
              The Customer is responsible for determining legally required retention periods for medical records,
              occupational health records, billing records, employer-service records, DOT records, OSHA records, and
              related data.
            </p>

            <h3>10.2 Service Metadata and User Telemetry</h3>
            <p>
              Snap retains Service Metadata, User Telemetry, access logs, security logs, audit logs, and operational records
              in accordance with Snap’s internal retention schedules, legal obligations, security requirements, audit
              requirements, and any applicable legal hold.
            </p>

            <h3>10.3 Employer Portal Records</h3>
            <p>
              Employers are responsible for maintaining independent copies of records they are legally required to retain,
              including records subject to DOT, OSHA, FMCSA, workers’ compensation, state drug testing, employment law,
              or other retention obligations.
            </p>
            <p>Access to Employer Portal Data through the Platform depends on the Customer’s active subscription and configuration.</p>

            <h2>11. Third-Party Links and Integrations</h2>
            <h3>11.1 Integrations</h3>
            <p>
              The Platform may interface with third-party systems, laboratories, clearinghouses, payment systems, identity
              providers, reference providers, state registries, federal registries, pharmacies, e-prescribing networks, MRO
              systems, or other external systems through integrations configured by the Customer.
            </p>

            <h3>11.2 Third-Party Practices</h3>
            <p>
              Snap is not responsible for the privacy, security, data handling, accuracy, availability, or compliance
              practices of third-party systems that are not controlled by Snap.
            </p>
            <p>Use of third-party systems may be subject to separate terms, privacy policies, authorizations, or legal requirements.</p>

            <h2>12. California Privacy Rights</h2>
            <h3>12.1 California Confidentiality of Medical Information Act</h3>
            <p>
              Snap is a California corporation. To the extent the California Confidentiality of Medical Information Act
              applies to Snap’s handling of medical information, Snap complies with its applicable obligations.
            </p>
            <p>Individuals with questions about medical information controlled by a Customer should contact the Customer responsible for the record.</p>

            <h3>12.2 California Consumer Privacy Laws</h3>
            <p>
              To the extent California consumer privacy laws apply to Service Metadata or User Telemetry processed about
              California residents in an individual capacity, California residents may have rights to request information
              about personal information collected, request deletion, request correction, or exercise other rights available
              under applicable law.
            </p>
            <p>Snap does not sell personal information as defined by California consumer privacy law.</p>

            <h3>12.3 Direct Marketing</h3>
            <p>Snap does not disclose personal information to third parties for their direct marketing purposes.</p>

            <h2>13. Children’s Privacy</h2>
            <p>
              The Platform is not directed to children under the age of 18 as end users. Snap does not knowingly collect
              personal information from children under 18 in a consumer capacity through the Platform.
            </p>
            <p>
              Clinical records or occupational health records relating to minors, if any, are controlled by the Customer
              and handled as Customer Data subject to applicable law and the BAA where PHI is involved.
            </p>

            <h2>14. HIPAA Complaints and PHI Requests</h2>
            <h3>14.1 PHI Requests</h3>
            <p>
              Requests for access, amendment, restriction, accounting of disclosures, or other HIPAA rights regarding PHI
              should be directed to the Customer or healthcare provider responsible for the record.
            </p>
            <p>
              Snap generally cannot fulfill PHI rights requests directly because Snap acts as a Business Associate and not
              as the Covered Entity.
            </p>

            <h3>14.2 Clinical User Questions</h3>
            <p>Clinical and Administrative Users should direct PHI-related questions to the Customer’s Privacy Officer or compliance contact.</p>

            <h3>14.3 Employer Portal User Questions</h3>
            <p>
              Employer Portal Users with questions about Employer Portal Data, occupational health outputs, or records made
              available through the Employer Command Center should contact the clinic or healthcare organization that made
              the information available through the Platform.
            </p>
            <p>Employer Portal Users should not assume that their own employer has a BAA with Snap or is a HIPAA Covered Entity.</p>

            <h3>14.4 Government Complaints</h3>
            <p>
              Individuals who believe their HIPAA rights have been violated may file a complaint with the U.S. Department of
              Health and Human Services, Office for Civil Rights.
            </p>
            <p>Filing a privacy complaint will not result in retaliation by Snap.</p>

            <h2>15. Privacy Contact</h2>
            <p>
              For questions, concerns, or requests regarding this Privacy Policy or Snap’s handling of Service Metadata or
              User Telemetry, contact:
            </p>
            <p>
              <strong>Privacy Officer / Privacy Inquiries</strong>
              <br />
              Snap Solutions Inc.
              <br />
              3838 Del Amo Blvd., Suite 202
              <br />
              Torrance, California 90503
              <br />
              <a href="mailto:support@snapsolutions.com">support@snapsolutions.com</a>
            </p>
            <p>For PHI-specific requests, contact the Customer or healthcare provider responsible for the record.</p>

            <h2>16. Updates to This Privacy Policy</h2>
            <p>Snap may update this Privacy Policy from time to time.</p>
            <p>
              If Snap makes material changes to how it handles Service Metadata, User Telemetry, or the core data-track
              structure described in this Privacy Policy, Snap will provide notice through an in-platform notification at
              next login, by email to the address associated with your account, or through another reasonable notice
              mechanism.
            </p>
            <p>The Last Modified date reflects the date of the most recent update.</p>
            <p>
              Continued use of the Platform after the effective date of an updated Privacy Policy constitutes acceptance of
              the updated Privacy Policy.
            </p>

            <h2>17. No Conflict with MSA or BAA</h2>
            <p>
              This Privacy Policy does not modify, expand, limit, or supersede the MSA, BAA, Order Form, or any other
              executed agreement between Snap and the Customer.
            </p>
            <p>In the event of any conflict:</p>
            <ul>
              <li>The BAA governs with respect to PHI and Business Associate obligations.</li>
              <li>The MSA governs with respect to Customer Data, commercial terms, service scope, data rights, and Customer obligations.</li>
            </ul>
            <p>
              This Privacy Policy governs only Snap’s user-facing disclosures regarding the data tracks described above,
              subject to the MSA and BAA.
            </p>
          </div>
        </section>

        <footer className="terms-footer-strip">
          <strong>Snap Solutions Inc.</strong> &nbsp;·&nbsp; <strong>Privacy Policy</strong> &nbsp;·&nbsp; <strong>Confidential</strong>
        </footer>
      </div>
    </div>
  );
}
